import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, X, Search as SearchIcon } from "lucide-react";

/**
 * Multi-select dropdown — replaces cluttered checkbox grids for fields with
 * many options (PPF areas, wrap areas, tint windows, etc.). Selected items
 * are shown as pills inside the trigger; the panel opens on click and
 * closes when the user clicks outside or presses Escape.
 *
 * Props:
 *   label      : label shown above the trigger (optional; caller may render its own)
 *   options    : string[] of option values (label === value)
 *   value      : string[] currently selected
 *   onChange   : (next: string[]) => void
 *   testId     : data-testid on the trigger button
 *   placeholder: text shown when nothing is selected
 *   chipGridOnMobile: when true, phones (<md) render an always-visible chip
 *     grid instead of the tap-to-open dropdown so installers can tick areas
 *     off in the field without extra taps. Desktop unchanged.
 */
export default function MultiSelectDropdown({
  label,
  options,
  value = [],
  onChange,
  testId,
  placeholder = "Select…",
  chipGridOnMobile = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const set = new Set(value || []);

  // Reset the filter whenever the panel closes so re-opening starts fresh.
  // Autofocus the search box on open so the user can start typing instantly.
  useEffect(() => {
    if (!open) {
      setQuery("");
    } else {
      // Defer focus to the next frame — the input isn't mounted yet when
      // this effect fires on the same tick as the state flip to open.
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (opt) => {
    const next = new Set(set);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(Array.from(next));
  };

  const clearOne = (opt, e) => {
    e.stopPropagation();
    const next = new Set(set);
    next.delete(opt);
    onChange(Array.from(next));
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const onTriggerKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen((v) => !v);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      {label && <label className="text-label block mb-1.5">{label}</label>}

      {/* Mobile: always-visible chip grid so installers can tap-toggle in the
          field without opening a dropdown. Desktop keeps the compact
          trigger + dropdown so office staff see many options in a scroll. */}
      {chipGridOnMobile && (
        <div
          data-testid={testId ? `${testId}-mobile-grid` : undefined}
          className="md:hidden flex flex-wrap gap-1.5"
        >
          {options.map((opt) => {
            const on = set.has(opt);
            return (
              <button
                key={opt}
                type="button"
                data-testid={testId ? `${testId}-chip-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}` : undefined}
                onClick={() => toggle(opt)}
                className={`text-xs rounded-full border px-3 py-1.5 transition ${
                  on
                    ? "bg-white text-black border-white font-semibold"
                    : "bg-white/[0.03] border-white/15 text-slate-300 hover:bg-white/[0.08]"
                }`}
              >
                {on && <Check className="inline w-3 h-3 mr-1 -mt-0.5" />}
                {opt}
              </button>
            );
          })}
        </div>
      )}

      {/* Desktop (or mobile fallback when chipGridOnMobile is off): compact
          tap-to-open dropdown with selected pills inside the trigger. */}
      <div className={chipGridOnMobile ? "hidden md:block" : "block"}>
      {/* NOTE: rendered as a <div role="button"> — the trigger must NOT be an
          actual <button> because it visually contains pill-remove and Clear
          buttons. Nested <button>s are invalid HTML and log a React hydration
          warning. Keeping it a div preserves the click/keyboard affordance
          without violating the interactive-inside-interactive rule. */}
      <div
        role="button"
        tabIndex={0}
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onTriggerKey}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30 text-left min-h-[42px] flex items-center gap-1.5 flex-wrap cursor-pointer"
      >
        {value.length === 0 ? (
          <span className="text-slate-500">{placeholder}</span>
        ) : (
          value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 bg-white/[0.08] border border-white/10 rounded-full px-2 py-0.5 text-[11px] text-white"
            >
              {v}
              <button
                type="button"
                data-testid={testId ? `${testId}-remove-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}` : undefined}
                onClick={(e) => clearOne(v, e)}
                className="text-slate-400 hover:text-white"
                aria-label={`Remove ${v}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
        <span className="ml-auto flex items-center gap-1 shrink-0">
          {value.length > 0 && (
            <button
              type="button"
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={clearAll}
              className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-white"
            >
              Clear
            </button>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
        </span>
      </div>

      {open && (
        <div
          data-testid={testId ? `${testId}-panel` : undefined}
          className="absolute z-30 mt-1 left-0 right-0 glass-strong border border-white/10 rounded-2xl p-1.5 shadow-2xl"
        >
          {/* Sticky search bar — shown whenever the list is more than a
              handful of options so long checklists (installation areas, PPF
              areas, tint windows, assignees, etc.) can be narrowed down in
              one keystroke. Short lists skip it so the panel stays compact. */}
          {options.length >= 6 && (
            <div className="relative mb-1.5">
              <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setOpen(false); }
                  // Stop clicks/typing from bubbling to the trigger div.
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
                data-testid={testId ? `${testId}-search` : undefined}
                placeholder={`Search ${options.length} options…`}
                className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
            </div>
          )}
          <div className="max-h-64 overflow-y-auto space-y-0.5 pr-0.5 -mr-0.5">
          {options.length === 0 && (
            <div className="text-xs text-slate-500 italic px-2 py-3 text-center">No options</div>
          )}
          {(() => {
            const needle = query.trim().toLowerCase();
            const filtered = needle
              ? options.filter((o) => String(o).toLowerCase().includes(needle))
              : options;
            if (options.length > 0 && filtered.length === 0) {
              return (
                <div
                  data-testid={testId ? `${testId}-empty` : undefined}
                  className="text-xs text-slate-500 italic px-2 py-3 text-center"
                >
                  No matches for &ldquo;{query}&rdquo;
                </div>
              );
            }
            return filtered.map((opt) => {
              const on = set.has(opt);
              return (
                <button
                  key={opt}
                  type="button"
                  data-testid={testId ? `${testId}-opt-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}` : undefined}
                  onClick={() => toggle(opt)}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-xl text-xs text-left transition ${
                    on ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/[0.06]"
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                    on ? "bg-emerald-400 border-emerald-300" : "border-white/30"
                  }`}>
                    {on && <Check className="w-2.5 h-2.5 text-black" />}
                  </span>
                  <span className="flex-1">{opt}</span>
                </button>
              );
            });
          })()}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
